import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ConfirmDialog from './confirmDialog';
import TooltipIconButton from './tooltipIconButton';
import { getUsedAiUsageCount } from './functions/getUsedAiUsageCount';
import { requestReportCreate } from './functions/requestReportCreate';
import SmartToyTwoToneIcon from '@mui/icons-material/SmartToyTwoTone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpIcon from '@mui/icons-material/Help';
import { CircularProgress, DialogTitle, Typography } from '@mui/material';
import { ScrollableItemList } from './scrollableItemList';
import { type SnackBarConfig } from './interfaces/SnackBar';
import SnackBarMessage from './snackBarMessage';
import { getTermsAndConditionsAgreement } from './functions/getTermsAndConditionsAgreement';
import { checkVersion } from './functions/checkVersion';
import Log from '@/Logger';
import { INSIGHT_REPORTER_CONFIG, INSIGHT_REPORTER_LINKS } from './constant';

type CsvRecord = {
    name: string
    serialId: string
    deviceDisplayName: string
    lastModified: string
    size?: number
    download?: any
}

// =============================================
// 定数定義
// =============================================
const NOT_AVAILABLE_FORMAT = { device: [INSIGHT_REPORTER_CONFIG.NAME] as readonly string[] };

// =============================================
// スタイル定義
// =============================================
const layoutStyles = {
    dialog: {
        height: '40vh',
    },

    icon: {
        color: '#1976d2',
    },

    content: {
        marginBottom: '1vh',
        marginLeft: '2vh',
        marginRight: '2vh',
    },

    termsSection: {
        marginTop: '3vh',
    },

    link: {
        margin: '0 0.5em',
    },

    usageCount: {
        width: '5vh',
    },

    spacing: {
        marginRight: '1vh',
        marginLeft: '1vh',
    },
} as const;

// =============================================
// 型定義
// =============================================
type InsightReporterProps = {
    CsvRecords: CsvRecord[]
};

type DialogHeaderProps = {
    currentAiUsageCount?: number
};

// =============================================
// ユーティリティ関数
// =============================================
const fileUtils = {
    /**
     * 利用可能なファイルをフィルタリング
     */
    filterAvailable: (records: CsvRecord[]): CsvRecord[] =>
        records.filter(file => !NOT_AVAILABLE_FORMAT.device.includes(file.serialId)),

    /**
     * ファイルパスからファイル名を取得
     */
    extractFileName: (path: string): string =>
        path.substring(path.lastIndexOf('/') + 1),
};

const validationUtils = {
    /**
     * ツールチップメッセージを生成
     */
    getTooltipMessage: (agreedTermsVersion: string | undefined, availableCount: number): string => {
        if (!agreedTermsVersion) {
            return 'テナント管理者が設定画面から機能を有効化して下さい';
        }
        if (!checkVersion(agreedTermsVersion, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.MINIMUM_VERSION)) {
            return 'テナント管理者が最新の規約に同意する必要があります';
        }
        if (availableCount === 0) {
            return 'CSVファイルを選択して下さい';
        }
        if (availableCount > INSIGHT_REPORTER_CONFIG.MAX_IMPORT_FILE) {
            return `${INSIGHT_REPORTER_CONFIG.MAX_IMPORT_FILE}件以上のCSVファイルが選択されています`;
        }
        return '';
    },
};

// =============================================
// カスタムフック
// =============================================
const useInsightReporter = (availableFiles: CsvRecord[]) => {
    const logger = Log.getLogger();
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [agreedTermsVersion, setAgreedTermsVersion] = useState<string>();
    const [availableAiUsageCount, setAvailableAiUsageCount] = useState<number>();
    const [snackBar, setSnackBar] = useState<SnackBarConfig>({
        isOpen: false,
        message: '',
        type: undefined
    });

    // 規約同意状況を取得
    useEffect(() => {
        getTermsAndConditionsAgreement(INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.NAME)
            .then(setAgreedTermsVersion)
            .catch(() => { });
    }, []);

    // 機能が利用可能かどうかの判定
    const isAvailable = useMemo(
        () => (availableAiUsageCount ?? 0) > 0 && 
              checkVersion(agreedTermsVersion, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.MINIMUM_VERSION),
        [availableAiUsageCount, agreedTermsVersion]
    );

    // スナックバーを閉じる処理
    const handleCloseSnackBar = useCallback((_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackBar(prev => ({ ...prev, isOpen: false }));
    }, []);

    // ダイアログを開く処理
    const handleOpenDialog = useCallback(async () => {
        logger.ui.info('InsightReporter', 'Open Dialog');
        setIsLoading(true);
        setIsOpenDialog(true);
        try {
            const usedAiUsageCount = await getUsedAiUsageCount('InsightReporter');
            setAvailableAiUsageCount(Math.max(INSIGHT_REPORTER_CONFIG.MAX_COUNT - usedAiUsageCount, 0));
        } catch {
            setSnackBar({ type: 'error', message: 'AI利用回数の取得に失敗しました', isOpen: true });
        } finally {
            setIsLoading(false);
        }
    }, [logger]);

    // ダイアログを閉じる処理
    const handleCloseDialog = useCallback(() => {
        logger.ui.info('InsightReporter', 'Close Dialog');
        setIsOpenDialog(false);
        setAvailableAiUsageCount(undefined);
    }, [logger]);

    // レポート生成リクエスト処理
    const handleCreateReportRequest = useCallback(async () => {
        logger.ui.info('InsightReporter', 'Click CreateReportRequestButton');
        setIsLoading(true);
        try {
            await requestReportCreate(
                availableFiles
                    .filter(file => !NOT_AVAILABLE_FORMAT.device.includes(file.serialId))
                    .map(file => file.name),
                'InsightReporter'
            );
            setSnackBar({
                type: 'success',
                message: 'AIレポート生成のリクエストに成功しました',
                isOpen: true,
            });
        } catch {
            setSnackBar({ type: 'error', message: 'AIレポート生成のリクエストに失敗しました', isOpen: true });
        } finally {
            handleCloseDialog();
            setIsLoading(false);
        }
    }, [availableFiles, handleCloseDialog, logger]);

    return {
        state: {
            isOpenDialog,
            isLoading,
            agreedTermsVersion,
            availableAiUsageCount,
            snackBar,
            isAvailable,
        },
        handlers: {
            handleOpenDialog,
            handleCloseDialog,
            handleCreateReportRequest,
            handleCloseSnackBar,
        },
    };
};

// =============================================
// サブコンポーネント
// =============================================
const DialogHeader: React.FC<DialogHeaderProps> = ({ currentAiUsageCount }) => (
    <>
        <AutoAwesomeIcon sx={layoutStyles.icon} />
        <Typography variant="h6" fontWeight="bold">
            {INSIGHT_REPORTER_CONFIG.NAME}
        </Typography>
        <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginLeft: 'auto'
        }}>
            <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ ...layoutStyles.spacing }}
            >
                残り
            </Typography>
            <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                    ...layoutStyles.usageCount,
                    ...layoutStyles.icon,
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {typeof currentAiUsageCount === 'number' ? Math.max(currentAiUsageCount, 0) : ''}
            </Typography>
            <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ ...layoutStyles.spacing }}
            >
                回
            </Typography>
            <TooltipIconButton
                isAvailable={false}
                onPress={() => {}}
                tooltipTitle={`この機能は1ヶ月間に${INSIGHT_REPORTER_CONFIG.MAX_COUNT}回利用できます`}
                relatedLink={INSIGHT_REPORTER_LINKS.MANUAL}
                icon={<HelpIcon sx={{ color: '#AAAAAA' }} />}
            />
        </DialogTitle>
    </>
);

const DialogContent: React.FC<{
    isLoading: boolean;
    isAvailable: boolean;
    availableCount: number;
    csvRecords: CsvRecord[];
}> = ({ isLoading, isAvailable, availableCount, csvRecords }) => {
    const logger = Log.getLogger();

    if (isLoading) {
        return (
            <Typography variant="body1" align="center" sx={{ marginTop: 2 }}>
                <CircularProgress />
            </Typography>
        );
    }

    return (
        <>
            <Typography
                variant="subtitle1"
                sx={{
                    ...layoutStyles.content,
                }}
            >
                {isAvailable
                    ? `CSVファイル（${availableCount}件）からレポートを自動生成します。`
                    : '現在この機能は利用できません。'}
            </Typography>
            {isAvailable && (
                <ScrollableItemList
                    itemList={csvRecords
                        .map(file => ({
                            label: fileUtils.extractFileName(file.name),
                            disable: NOT_AVAILABLE_FORMAT.device.includes(file.serialId),
                        }))
                        .sort((a, b) => (a.disable === b.disable ? 0 : a.disable ? 1 : -1))}
                    disableMessage={`${INSIGHT_REPORTER_CONFIG.NAME}によって作成されたファイルは読み込みできません`}
                />
            )}
            <Typography
                variant="subtitle1"
                sx={{
                    ...layoutStyles.termsSection,
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: 'bold',
                }}
            >
                利用にあたって
                <a
                    href={INSIGHT_REPORTER_LINKS.INFORMATION}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        textDecoration: 'underline',
                        ...layoutStyles.link,
                        ...layoutStyles.icon
                    }}
                    onClick={() => logger.ui.info('InsightReporter', 'Click TermsOfServiceLink')}
                >
                    本機能に関するご案内
                </a>
                を確認して下さい
            </Typography>
        </>
    );
};

// =============================================
// メインコンポーネント
// =============================================
/**
 * AIレポート生成機能を提供するコンポーネント
 *
 * CSVレコードを基にAIを使用してレポートを生成する機能を提供します。
 * ユーザーの利用規約同意状況、ファイル数制限、AIの利用回数制限などを考慮して
 * 機能の利用可否を判定し、適切なUI状態を表示します。
 * 
 * @component
 * @param props - コンポーネントのプロパティ
 * @param props.CsvRecords - レポート生成対象となるCSVレコードの配列
 * 
 * @returns レポート生成ボタン、確認ダイアログ、スナックバーメッセージを含むReactエレメント
 * 
 * @example
 * ```tsx
 * const csvData = [
 *   { id: 1, name: 'sample1.csv', data: [...] },
 *   { id: 2, name: 'sample2.csv', data: [...] }
 * ];
 * 
 * <InsightReporter CsvRecords={csvData} />
 * ```
 * 
 * @remarks
 * - 利用可能なファイル数がMAX_IMPORT_FILE以下の場合のみ機能が有効
 * - ユーザーが最新の利用規約に同意している場合のみ機能が有効
 * - AIの利用回数制限に達していない場合のみ機能が有効
 * - ファイルが選択されていない場合は適切なエラーメッセージを表示
 */
export default function InsightReporter({ CsvRecords }: InsightReporterProps): React.ReactElement | null {
    // 利用可能なファイルをフィルタリング
    const availableFiles = useMemo(() => fileUtils.filterAvailable(CsvRecords), [CsvRecords]);
    const availableCount = availableFiles.length;

    // カスタムフックで状態管理
    const { state, handlers } = useInsightReporter(availableFiles);

    // ツールチップメッセージを生成
    const tooltipMessage = useMemo(
        () => validationUtils.getTooltipMessage(state.agreedTermsVersion, availableCount),
        [state.agreedTermsVersion, availableCount]
    );

    // ダイアログ内容をレンダリング
    const renderDialogContents = () => (
        <div style={{
            ...layoutStyles.dialog,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}>
            <DialogContent
                isLoading={state.isLoading}
                isAvailable={state.isAvailable}
                availableCount={availableCount}
                csvRecords={CsvRecords}
            />
        </div>
    );

    // ダイアログボタンの設定
    const dialogButtonList = state.isAvailable
        ? [
            {
                label: 'キャンセル',
                labelColor: '#1976d2',
                buttonColor: '#FFFFFF',
                hoverColor: '#DDDDDD',
                onPress: handlers.handleCloseDialog,
                isPressable: !state.isLoading,
            },
            {
                label: 'レポートを生成',
                labelColor: '#FFFFFF',
                buttonColor: '#1976d2',
                hoverColor: '#1565c0',
                onPress: handlers.handleCreateReportRequest,
                isPressable: !state.isLoading,
            },
        ]
        : [
            {
                label: 'OK',
                labelColor: '#1976d2',
                buttonColor: '#FFFFFF',
                hoverColor: '#DDDDDD',
                onPress: handlers.handleCloseDialog,
                isPressable: !state.isLoading,
            },
        ];

    return (
        <>
            <SnackBarMessage
                snackBar={state.snackBar}
                handleCloseSnackBar={handlers.handleCloseSnackBar}
            />
            <ConfirmDialog
                isOpenDialog={state.isOpenDialog}
                onClose={handlers.handleCloseDialog}
                header={<DialogHeader currentAiUsageCount={state.availableAiUsageCount} />}
                contents={renderDialogContents()}
                buttonList={dialogButtonList}
            />
            <TooltipIconButton
                isAvailable={
                    !state.isLoading && 
                    availableCount > 0 && 
                    availableCount <= INSIGHT_REPORTER_CONFIG.MAX_IMPORT_FILE && 
                    checkVersion(state.agreedTermsVersion, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.MINIMUM_VERSION)
                }
                onPress={handlers.handleOpenDialog}
                tooltipTitle={INSIGHT_REPORTER_CONFIG.NAME}
                tooltipMessage={tooltipMessage}
                relatedLink={INSIGHT_REPORTER_LINKS.MANUAL}
                icon={<SmartToyTwoToneIcon sx={{ display: 'block'}} />}
                isLocked={!checkVersion(state.agreedTermsVersion, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.MINIMUM_VERSION)}
            />
        </>
    );
}
